import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { LocationAddressInterface } from '@interfaces/location-address.interface';
import { BaseDropdownInterface } from '@interfaces/base-dropdown.interface';
import { AirportCodeInterface } from '@interfaces/airport-code.interface';
import { environment } from '@environments/environment';
import { LocationInterface } from '../../pages/v2/create-service-request/modals/add-location-with-verification/interfaces/location.interface';

@Injectable({
  providedIn: 'root',
})
export class LocationV2Service {
  private readonly addressTypes: string[] = ['Residential', 'Hospital', 'Other'];

  constructor(private httpClient: HttpClient) {}

  /**
   * save new location
   * @param payload - payload details
   * @returns - saved location id
   */
  saveLocation(payload: LocationInterface): Observable<string> {
    const url = `${environment.apiHostV2}/addresses`;

    return this.httpClient.post<string>(url, payload)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  /**
   * get airport codes
   */
  getAirportCodeList(): Observable<AirportCodeInterface[]> {
    const url = `${environment.apiHost}/address/GetAllAirportCodeList`;

    return this.httpClient.get<AirportCodeInterface[]>(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  /**
   * get location address list
   * @param searchText - search value
   * @param addressType - address type value
   * @param businessUnitId - business unit id
   * @param airportCode - airport code
   */
  getLocationAddressList(
    searchText: string,
    addressType: string,
    businessUnitId: string,
    airportCode?: string,
  ): Observable<LocationAddressInterface[]> {
    let url = `${environment.apiHostV2}/addresses/search?searchText=${encodeURIComponent(searchText)}&addressType=${encodeURIComponent(addressType)}&businessUnitId=${encodeURIComponent(businessUnitId)}`;

    if (airportCode) {
      url += `&airportCodes=${airportCode}`;
    }

    return this.httpClient.get<LocationAddressInterface[]>(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  /**
   * get location address suggestions
   * @param customerId - id of the customer to show suggestions
   * @param addressType - address type value
   * @param airportCode - airport code to search
   */
  getLocationAddressSuggestions(
    customerId: string,
    addressType: string,
    airportCode: string | null = null
  ): Observable<LocationAddressInterface[]> {
    const airportCodeQuery = airportCode ? `&airportCodes=${airportCode}` : '';

    const url = `${environment.apiHostV2}/addresses/customer/${customerId}/search?addressType=${encodeURIComponent(addressType)}${airportCodeQuery}`;

    return this.httpClient.get<LocationAddressInterface[]>(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  /**
   * get address types
   */
  getAddressTypes(): Observable<string[]> {
    return of(this.addressTypes);
  }

  /**
   * get states
   */
  getStates(): Observable<BaseDropdownInterface[]> {
    const url = `${environment.apiHostV2}/states`;

    return this.httpClient.get<BaseDropdownInterface[]>(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  /**
   * get cities by state id
   * @param stateId - state id
   */
  getCitiesByStateId(stateId: string): Observable<BaseDropdownInterface[]> {
    const url = `${environment.apiHostV2}/states/${stateId}/cities`;

    return this.httpClient.get<BaseDropdownInterface[]>(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

}
